import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import {
  TabPanel,
  TabList as MuiTabList,
  TabListProps as MuiTabListProps,
  TabContext,
} from "@mui/lab";
import { styled, Box, Tab, SxProps, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import {
  segmentedPillBg,
  segmentedPillShadow,
  segmentedTrackBg,
} from "../../../theme/segmentedChrome";

/**
 * Gap between the selected pill and the track, on every side.
 *
 * The track is 48 and the pill 40, so 4 is what the vertical arithmetic already
 * implies; naming it lets the track's radius be derived from it rather than
 * guessed (see `trackRadius`).
 */
const TAB_INSET = 4;

/**
 * Above this, `theme.shape.borderRadius * 3` stops being a rounded rect and
 * becomes a pill.
 *
 * `* 3` was calibrated against ONE theme — `@repo/ui`/platform, whose
 * `shape.borderRadius` is 4, so the track renders at 12 on a 48px-tall control.
 * `apps/app` sets `shape.borderRadius` from the tenant's brand `border_radius`
 * token, which is 10 by default: the same expression renders 30, the browser
 * clamps it to half the height, and the track becomes a lozenge wrapped around
 * square-cornered pills. Measured: track radius 30px, pill radius 10px.
 *
 * This is the same class of bug as `theme.spacing()` inside a layer-1 component
 * (working-conventions §47) — a multiplier that is only correct under one of the
 * two themes this component is mounted by — and it takes the same shape of fix
 * the app already uses for fields (`FIELD_RADIUS_MAX` in
 * `apps/app/src/components/UI/fieldMetrics.ts`): a CEILING, not a fixed value,
 * so a theme with a modest token is left exactly as it was.
 */
const TRACK_RADIUS_CEILING = 12;

/**
 * Track radius for a given theme radius.
 *
 * Under the ceiling, `* 3` verbatim — the platform's rendering is unchanged.
 * Over it, fall back to the concentric-radius rule (inner radius + the gap
 * around it), which is what keeps the pill's corners parallel to the track's
 * instead of one being visibly rounder than the other.
 */
const trackRadius = (themeRadius: unknown): number => {
  const base = Number(themeRadius);
  if (!Number.isFinite(base)) return TRACK_RADIUS_CEILING;
  const scaled = base * 3;
  return scaled <= TRACK_RADIUS_CEILING ? scaled : base + TAB_INSET;
};

/*
 * The track fill used to be a local `trackFill` helper here. It now comes from
 * `theme/segmentedChrome` (`segmentedTrackBg`), alongside the pill, so the two
 * rungs of one ladder are calibrated together — see the note at the call site.
 *
 * ⚠️ One finding from the deleted docblock is worth keeping, because it is about
 * METHOD rather than about a value: this file briefly carried a 0.12 track,
 * calibrated to land on `#27272a` on the stated grounds that this was "precisely
 * the `grey[100]` the platform's own `SegmentedTabs` already uses for this same
 * track". The premise was false — `SegmentedTabs.tsx` hardcodes its own
 * `grey[100]` and shares no literal with this file — and the result composited
 * ~30 levels above its ground against light mode's ~8, reported as the Labs
 * header controls being "too light" next to Studio's. An unverified sentence in
 * a comment set a colour. Verify the other side before calibrating against it.
 */

const StyledMuiTabList = styled(MuiTabList)<MuiTabListProps>(({ theme }) => ({
  "& .MuiTabs-indicator": {
    display: "none",
  },
  borderBottom: "none",
  paddingLeft: 0,
  paddingRight: 0,
  color: "inherit",
  /*
    MODE-AWARE, from the single source in `theme/segmentedChrome`.

    In light mode this is the same 3% black wash it has been since it was
    calibrated (~#F7F7F7 over white — lighter than grey[100], without falling to
    the grey[50] that read flat in `apps/app`). Light rendering is unchanged.

    What it fixes is dark mode. A BLACK wash on a near-black surface is a no-op:
    `alpha(black, 0.03)` over the platform's dark card `#0f0f12` is a step of
    under half a channel value, so the track vanished — and with the pill also
    resolving to the card's own colour (see the pill below), the whole control
    collapsed to a row of bare labels distinguishable only by font-weight. That
    is what was reported against Studio's "Logo alignment" / "Logo size".

    DARK — the wash has to FLIP, and the ladder with it. A 3% black wash over an
    already-dark ground is not "subtle", it is nothing: measured on the platform's
    dark card (#0f0f12) the track resolved to #0f0f11, ΔL* 0.03 from its own
    ground. The track vanished and the control read as two loose icons rather
    than a segmented switch. Reported as "dark mode theme toggle needs subtle bg
    fill like light mode has", against Studio's EMAIL tab sun/moon control.

    So dark washes with WHITE, and the direction inverts with it. In light the
    track is a RECESS — a step BELOW the surface, with the pill returning to
    surface level and a shadow lifting it. Near black neither half of that is
    available: there is nowhere below (the page itself is #09090b, so a darker
    groove reads as a hole punched through the card), and the pill's shadow is
    rgba(16,24,40,…) — near-black on near-black, contributing nothing. Dark
    therefore uses the elevation-by-lightness ladder instead: ground → track →
    pill, each one white wash lighter than the last. See the pill's own note for
    the second rung.

    ⚠️ THE TRACK VALUE IS NOT CALIBRATED IN THIS FILE ANY MORE, and this branch's
    own measurement of it is superseded rather than merged. VRG-560 and VRG-512
    independently fixed this same invisible-in-dark-mode bug and landed on
    different LADDERS, not just different numbers: 0.04 track / 0.06 pill here,
    0.05 / 0.10 on main. The pill resolved to main's `segmentedPillBg` (0.10), so
    keeping this branch's 0.04 track would have paired a track measured against a
    0.06 pill with a 0.10 one — a ramp neither branch ever rendered. The ΔL*
    table that justified 0.04 (rejecting 0.06 as "a grey slab competing with the
    card") was measured when track and pill were the SAME value, so its objection
    does not transfer to a ladder where the pill is now clearly above the track.

    Both rungs therefore come from `theme/segmentedChrome`, which is where the
    composited ramps for the shipped pair are recorded (card #0f0f12 → #1b1b1e →
    #323234; page #09090b → #151517 → #2c2c2e). Calibrate there, on both rungs at
    once — a track tuned against a pill in another file is how the two drift.

    ⚠️ A comment here previously asserted that the platform's `SegmentedTabs`
    "shares this track by design", and changed this value on that basis. It does
    not share it — it hardcodes its own `grey[100]`, which the platform remaps to
    a LIGHT zinc in dark mode, so `SegmentedTabs` kept a visible track while this
    control lost one. The two are one design that has drifted; see the note in
    `segmentedChrome` for why converging them is not bundled in here.

    That false claim is not trivia: it is the entire justification the 0.12 track
    was picked on ("land on the grey[100] SegmentedTabs already uses"), so an
    unchecked sentence in a comment set a colour. Verify the other side before
    calibrating against it.
  */
  backgroundColor: segmentedTrackBg(theme),
  borderRadius: trackRadius(theme.shape.borderRadius),
  maxHeight: 48,
  minHeight: 48,
}));

interface TabsProps {
  tabs: tabsType;
  tab?: string;
  onChange?: (value: string) => void;
  children?: React.ReactNode;
  sx?: SxProps;
  /**
   * Stretch to the container and split the halves evenly.
   *
   * The wrapper is `fit-content` by default, which is right for a toolbar chip
   * (the platform's billing period sits inline beside other controls) and wrong
   * for a control that IS the row — in a bottom sheet a shrink-wrapped toggle
   * floats against the left edge with dead space beside it.
   */
  fullWidth?: boolean;
}

type tabsType = {
  label: string;
  value: string;
  /** Optional leading icon, rendered inline before the label. */
  icon?: React.ReactNode;
  /**
   * Accessible name. Required when the caller hides the visible label (e.g. an
   * icon-only toggle in a tight mobile row) — without it the tab has no accessible
   * name and the role="tab" / aria-selected semantics convey nothing.
   */
  ariaLabel?: string;
  component?: React.ReactNode;
}[];

/**
 * The measured box of the active tab, in the wrapper's coordinate space.
 *
 * Everything here is READ FROM THE DOM rather than assumed, because this
 * component renders under two themes that disagree about its geometry: the tab
 * widths are label-dependent ("List" and "Grid" are not the same width, so a
 * 50% translate would be wrong), and the radius comes from whichever theme is
 * mounting (see the `borderRadius` note on the Tab below — `apps/app` wins that
 * one with an inline style). Measuring sidesteps both.
 */
type PillRect = {
  left: number;
  top: number;
  width: number;
  height: number;
  radius: string;
};

/**
 * The pill's travel. Verbatim from the platform's `SegmentedTabs` — this is the
 * "reuse the component from platform" half of the change; the two controls are
 * one design (see the `.Mui-selected` note below) and must now also move alike.
 */
const PILL_TRANSITION =
  "left 0.32s cubic-bezier(0.65, 0, 0.35, 1), width 0.32s cubic-bezier(0.65, 0, 0.35, 1)";

export const ToggleButton = (props: TabsProps) => {
  const { tabs, sx, onChange = () => {}, tab, fullWidth = false } = props;
  const theme = useTheme();
  /*
   * Reduced motion degrades to the FINISHED state, never to a missing or
   * mispositioned one: `transition: none` means left/width jump straight to the
   * measured values, so the pill still lands exactly on the selected half — it
   * simply does not travel there.
   */
  const prefersReducedMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [pill, setPill] = useState<PillRect | null>(null);

  /**
   * Has the pill ever been placed? Drives ONE thing: whether its first landing
   * is allowed to animate. It must not be.
   *
   * The pill's resting values start at `left: 0, width: 0`, and the travel
   * transition below is unconditional, so the FIRST measurement animated like
   * any other move — the pill grew out of the left edge and slid to the selected
   * half every time the surface opened. On the certificates Layout sheet, which
   * opens with a selection already made, that reads as the control deciding
   * something in front of you rather than showing you what is already true.
   * Reported as "awkward animation on the left when opening it — it should just
   * be preselected already."
   *
   * `opacity: pill ? 1 : 0` did not cover this: opacity flips to 1 in the same
   * commit that writes the measured left/width, so the pill became visible
   * exactly as the slide began. It prevented a paint at the origin, not the
   * animation away from it.
   *
   * A REF checked during render, deliberately — not an rAF or a timeout. The
   * render where `pill` first becomes non-null is the one that must be
   * transition-less; the effect then marks it placed, and the next render is
   * whatever the user does (a tab click), by which point travel is enabled. So
   * the handoff needs no frame-timing guess and cannot leave the transition
   * permanently off. That matters because `requestAnimationFrame` does not fire
   * at all in some embedded/preview contexts, which would strand an rAF-gated
   * flag at `false` and silently kill the animation this component exists for.
   */
  const hasPlacedRef = useRef(false);
  const isFirstPlacement = pill !== null && !hasPlacedRef.current;
  useEffect(() => {
    if (pill) hasPlacedRef.current = true;
  }, [pill]);

  const activeIndex = tabs.findIndex((t) => t.value === tab);

  /*
   * ONE pill element, moved — not a per-tab background toggled on and off.
   *
   * This is the whole fix. The selected fill used to live on the Tab's own
   * `&.Mui-selected` background, which meant there was never an object to
   * travel: the outgoing half faded its background out while the incoming one
   * faded its background in, and since `background.paper` on a `grey[100]`
   * track is a small step, that cross-fade read as a flash rather than as
   * motion. A transition on `background-color` cannot express "the selection
   * moved" no matter how long you make it, because the thing that changed is in
   * two places at once.
   *
   * So the fill is hoisted out of the tabs into a single absolutely-positioned
   * element whose `left`/`width` animate between the measured tab boxes — the
   * same mechanic `SegmentedTabs` has used since it was written, now on the
   * shared base so BOTH apps get it (working-conventions §47: improve layer 1
   * rather than import the platform's component or fork a copy).
   */
  useLayoutEffect(() => {
    const measure = () => {
      const wrapper = wrapperRef.current;
      const list = listRef.current;
      if (!wrapper || !list) return;

      const btn =
        activeIndex >= 0
          ? list.querySelectorAll<HTMLElement>('[role="tab"]')[activeIndex]
          : undefined;
      if (!btn) {
        setPill(null);
        return;
      }

      // Not laid out yet — a closed bottom sheet measures 0 and would park the
      // pill at the origin. Leave the previous rect until it has real size.
      if (btn.offsetWidth === 0) return;

      /*
       * OFFSETS, NOT `getBoundingClientRect`. This is not a style preference —
       * a rect is the element's VISUAL box, so it includes any ancestor
       * `transform`, and this control's main consumer lives inside a bottom
       * sheet that opens by animating one. Measuring on the first frame of that
       * animation returned the tab scaled to ~0.75 (a 125.6x40 tab read as
       * 94.2x22.5), those scaled numbers were written to the pill's `left` and
       * `width` as literal px, and nothing re-measured afterwards — the sheet's
       * transform changes no layout, so the ResizeObserver below never fires.
       * The pill settled undersized and adrift, in the one place it is used
       * most. `offsetLeft`/`offsetWidth` are layout values and ignore
       * transforms entirely, which is why `SegmentedTabs` reads them too.
       *
       * The walk is needed because `offsetLeft` is relative to the nearest
       * POSITIONED ancestor, and MUI puts one between us and the tab
       * (`.MuiTabs-scroller` is `position: relative`), so a bare `offsetLeft`
       * would be measured from the wrong origin.
       */
      let left = 0;
      let top = 0;
      let node: HTMLElement | null = btn;
      while (node && node !== wrapper) {
        left += node.offsetLeft;
        top += node.offsetTop;
        const parent = node.offsetParent as HTMLElement | null;
        // An intermediate scroller shifts the tab but not the pill (which is
        // positioned on the wrapper, outside it), so discount its scroll.
        if (parent && parent !== wrapper) {
          left -= parent.scrollLeft;
          top -= parent.scrollTop;
        }
        node = parent;
      }

      const next: PillRect = {
        left,
        top,
        width: btn.offsetWidth,
        height: btn.offsetHeight,
        radius: window.getComputedStyle(btn).borderRadius,
      };

      /*
       * Bail out when nothing moved. Not an optimisation — `tabs` is an array
       * literal at most call sites, so it is a new identity every render and
       * this effect re-runs every render; an unconditional `setPill` would then
       * re-render, re-run, and loop forever.
       */
      setPill((prev) =>
        prev &&
        prev.left === next.left &&
        prev.top === next.top &&
        prev.width === next.width &&
        prev.height === next.height &&
        prev.radius === next.radius
          ? prev
          : next,
      );
    };

    measure();
    // Re-measure once the browser has applied the active tab's font-weight
    // reflow, which changes the tab's width and so the pill's target.
    const raf = requestAnimationFrame(measure);
    const observer =
      typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    if (observer && listRef.current) observer.observe(listRef.current);
    window.addEventListener("resize", measure);

    return () => {
      cancelAnimationFrame(raf);
      observer?.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [activeIndex, tabs]);

  const handleChange = (event: React.SyntheticEvent, tab: string) => {
    onChange(tab);
  };

  return (
    <Box
      ref={wrapperRef}
      sx={{
        position: "relative",
        width: fullWidth ? "100%" : "fit-content",
      }}
    >
      {/*
        Rendered BEFORE the tab list and pinned to `zIndex: 0`, with the tabs at
        `zIndex: 1`, so the stacking order is explicit rather than inherited from
        source order: track background (unpositioned) < pill < labels.
      */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          left: pill ? `${pill.left}px` : 0,
          top: pill ? `${pill.top}px` : 0,
          width: pill ? `${pill.width}px` : 0,
          height: pill ? `${pill.height}px` : 0,
          borderRadius: pill?.radius ?? 0,
          /*
           * The raised surface that carries the selection, from the single
           * source in `theme/segmentedChrome`.
           *
           * Light mode is `background.paper` exactly as before — see the note on
           * the Tab below for why that is theme-following rather than a
           * hardcoded white.
           *
           * Dark mode is the fix. `background.paper` is only the right pill fill
           * while the control sits on `background.default`; mounted inside a
           * `Card` — which IS `background.paper` — the pill and its own
           * container resolve to the same colour. Measured on Studio's "Logo
           * alignment": card `rgb(15,15,18)`, pill `rgb(15,15,18)`, opacity 1,
           * correctly positioned at left 4px / 60x40. It was rendering
           * perfectly and was invisible, which is why the bug read as "the
           * toggle buttons are gone" rather than as a colour being off.
           *
           * A wash composites over whatever is beneath it, so the pill stays
           * above the track on a card, on the bare page, or in a sheet — the
           * property the absolute token did not have.
           */
          backgroundColor: segmentedPillBg(theme),
          boxShadow: segmentedPillShadow(theme),
          // Hidden until the first measurement, so it never paints at the origin.
          opacity: pill ? 1 : 0,
          zIndex: 0,
          pointerEvents: "none",
          // No travel on the FIRST landing — it appears already parked on the
          // selected half. See `hasPlacedRef`.
          transition:
            prefersReducedMotion || isFirstPlacement ? "none" : PILL_TRANSITION,
        }}
      />
      <TabContext value={tab}>
        <StyledMuiTabList
          ref={listRef}
          sx={sx}
          onChange={handleChange}
          textColor="primary"
          indicatorColor="secondary"
        >
          {tabs.map((tab, index) => {
            return (
              <Tab
                key={index}
                sx={{
                  // Unselected: dimmer than the selected tab (see &.Mui-selected below).
                  color: theme.palette.text.secondary,
                  fontSize: 16,
                  /*
                   * 400 — set EXPLICITLY, because the inherited value was invisible.
                   *
                   * MUI's `MuiTab` root spreads `theme.typography.button`, whose
                   * weight is 500 on both themes (neither app overrides it), so
                   * the unselected half rendered 500 without anything in this file
                   * saying so. Measured in `apps/app`: 500 unselected / 600
                   * selected, both at 16px.
                   *
                   * Lowered with the selected weight below (600/500 -> 500/400) so
                   * the one-step delta that carries selection is preserved. See the
                   * fontWeight note in `&.Mui-selected` for why the pair moved.
                   */
                  fontWeight: 400,
                  /*
                   * Deliberately NOT `!important`, unlike the margin below.
                   *
                   * `apps/app`'s `MuiTab.defaultProps.style` also carries
                   * `borderRadius: buttonRadius` — the tenant's brand
                   * `border_radius` token, i.e. the exact radius every Button in
                   * that app renders at (measured: 10px on both). Letting the
                   * inline style win there is the RIGHT outcome: a segmented
                   * toggle's halves are buttons and should match the app's
                   * buttons. `trackRadius` is what has to follow it, which is
                   * why its over-ceiling branch is `base + TAB_INSET` — the
                   * radius the theme gave the pill, plus the gap around it.
                   */
                  borderRadius: 3,
                  /*
                   * ABSOLUTE px, not spacing multipliers (working-conventions §47).
                   * A layer-1 component renders under whichever app's theme mounts
                   * it, and the two disagree: `@repo/ui`/platform put spacing on a
                   * 4px base while `apps/app` never overrides MUI's default 8px. So
                   * `padding: 2` was 8px on the platform and 16px in the app — the
                   * same control, twice the size, with no way to correct it from
                   * inside the component. These values are the platform's rendering
                   * verbatim, so nothing there changes.
                   */
                  padding: '8px',
                  paddingLeft: '16px',
                  paddingRight: '16px',
                  /*
                   * `!important`, and it is load-bearing rather than defensive.
                   *
                   * `apps/app`'s theme carries `MuiTab.defaultProps.style` —
                   * an INLINE style, which beats any `sx` — setting
                   * `margin: '0 6px 4px'` for its underline tabs. Under that
                   * theme the pill's TOP inset was 0, so a 40px pill sat flush
                   * against the top edge of a 48px track and its corners were
                   * clipped by the track's own radius: measured pill y=160 on
                   * track y=160, i.e. the pill escaping the track's rounded
                   * corner. A stylesheet `!important` is the only thing that
                   * outranks an inline style, so this is what restores the
                   * even inset the track's geometry assumes.
                   *
                   * The platform's theme sets no such style, so its rendering
                   * is byte-identical to before.
                   */
                  margin: `${TAB_INSET}px !important`,
                  minHeight: 40,
                  maxHeight: 40,
                  minWidth: "fit-content",
                  textTransform: "none",
                  gap: '4px',
                  /*
                   * Above the pill, and positioned so the z-index applies. The
                   * label has to sit ON the moving fill, not under it.
                   */
                  position: "relative",
                  zIndex: 1,
                  /*
                   * COLOUR ONLY. It used to also transition `background-color`
                   * and `box-shadow`, which is what made the control read as a
                   * flash: those two properties were the entire selection
                   * treatment, and cross-fading them between two tabs is a
                   * dissolve, not a movement. Both now live on the pill, which
                   * animates its POSITION instead. What remains here is the
                   * secondary→primary text step, which genuinely is a fade and
                   * is timed shorter so the label brightens as the pill arrives
                   * rather than after it.
                   *
                   * It stays on the Tab rather than inside `.Mui-selected` for
                   * the original reason: the OUTGOING tab needs it too, and a
                   * rule scoped to the selected tab stops applying the instant
                   * the tab stops being selected. (It was also once a kebab-case
                   * `-webkit-transition` key, which emotion rejects at runtime,
                   * so for a while nothing animated at all.)
                   */
                  transition: theme.transitions.create("color", {
                    duration: theme.transitions.duration.shorter,
                  }),
                  // Keep the icon inline with the label (MUI stacks it above by default).
                  "& .MuiTab-iconWrapper": { margin: 0 },
                  /*
                   * The selected pill lives HERE, on the Tab's own `sx`, not on
                   * the parent TabList as `& .Mui-selected`.
                   *
                   * MUI applies `sx` after a theme's `styleOverrides`, so this
                   * is the only placement a consuming theme cannot outrank.
                   * From the parent it was a tie on specificity (0,2,0 either
                   * way) decided by source order, and `apps/app`'s theme wins
                   * that tie with
                   * `MuiTab.styleOverrides.root['&.Mui-selected'].backgroundColor
                   *  = primary.main + '15 !important'`. Measured under that
                   * theme: the selected half rendered rgba(0,0,0,0.082) — a
                   * translucent BLACK tint, i.e. DARKER than its own grey[100]
                   * track. That is the inverted-pill failure the note below
                   * describes, arriving from a consumer's theme rather than
                   * from a missing palette key.
                   *
                   * WHY THIS IS NO LONGER `common.white` / `common.black`
                   * (superseded 2026-08-22 — do not revert to the hardcoded
                   * pair; the note it replaced argued for them at length).
                   *
                   * `common.white` is guaranteed by MUI on every theme, which
                   * is what it was chosen for: the bug before it was
                   * `white[100]`, a CUSTOM @repo/ui palette key absent from
                   * MUI's Palette type, so a consumer theme that did not
                   * declare it got no fill at all. But "guaranteed to exist"
                   * was solving the wrong problem. A hardcoded fill does not
                   * follow the theme, so in DARK mode the selected half
                   * rendered pure rgb(255,255,255) on a rgb(39,39,42) track —
                   * a glaring white chip on a dark page, and the loudest thing
                   * on the Studio card. Reported by the user against Studio's
                   * "Logo alignment" / "Logo size".
                   *
                   * `background.paper` is equally guaranteed by MUI AND follows
                   * the theme, so it fixes the dark-mode blowout without
                   * reopening the missing-key bug. It is also what the
                   * platform's `SegmentedTabs` uses for its own pill
                   * (`SegmentedTabs.tsx:248`, the Billing / Roles tab control),
                   * so the two land on one value rather than each guessing.
                   *
                   * ⚠️ This paragraph used to add "measured identical `grey[100]`
                   * tracks on both". That was never true and is deleted rather
                   * than softened: `SegmentedTabs` hardcodes `grey[100]` while
                   * this file washes an alpha over its ground (see
                   * `segmentedTrackBg` in `theme/segmentedChrome`),
                   * so the TRACKS agree only by coincidence of one palette. The
                   * PILLS do share `background.paper` — in LIGHT. Dark now takes
                   * a wash here too, so even that agreement is light-only.
                   *
                   * The claim mattered: it is what the 0.12 track was calibrated
                   * against. See `theme/segmentedChrome`.
                   *
                   * color: `text.primary`, and it is now correct for the same
                   * reason it was previously WRONG. The old note is right that
                   * `text.primary` resolves to rgb(250,250,250) in dark mode
                   * and vanished — but that was white-on-white only because
                   * the pill had been forced white underneath it. MUI defines
                   * `text.primary` as the legible foreground for
                   * `background.*`, so pairing them is what makes the label
                   * follow the fill instead of fighting it. The old note's own
                   * closing rule — "when a fill stops following the theme, its
                   * text has to stop in the same edit" — applies in reverse
                   * here: the fill follows the theme again, so the text must
                   * too, in this same edit.
                   *
                   * fontWeight: the compensating signal, and the PAIR is what
                   * matters, not the numbers.
                   *
                   * A `background.paper` pill is a quieter fill than a white one,
                   * so selection is carried by weight + the brighter
                   * `text.primary` as well as by the fill. That reasoning stands
                   * and is why the weight is not simply deleted.
                   *
                   * What was wrong was borrowing `SegmentedTabs`' 600/500
                   * VERBATIM. The two controls do not render at the same size:
                   * `SegmentedTabs` sets `fontSize: 14`, this sets 16. Weight and
                   * size compound, so the same 600 lays down visibly more ink
                   * here than there — copying the number did not copy the
                   * appearance, which is the whole reason the two were supposed
                   * to match. Measured in `apps/app`'s Display sheet: the
                   * selected half was the heaviest 16px text on the surface, at
                   * 600 against the sheet's own option rows at 400 and the app's
                   * bottom-nav tab labels at 400.
                   *
                   * So the pair drops one step, 600/500 -> 500/400, keeping the
                   * one-step delta intact. Selection is still carried by four
                   * things — the `background.paper` fill, the shadow below,
                   * `text.primary` against `text.secondary`, and that delta — of
                   * which weight was only ever the compensating one.
                   *
                   * boxShadow: `SegmentedTabs`' shadow verbatim. The old
                   * heavier pair existed to make a white chip look raised; on
                   * a surface-coloured pill it renders as a smudge in light
                   * mode and as nothing at all in dark.
                   *
                   * WHERE THE FILL WENT (2026-08-23). The `background.paper`
                   * fill and that shadow are no longer HERE — they moved to the
                   * single sliding pill above, for the reason given there: a
                   * per-tab background has nothing to travel with, so it can
                   * only cross-fade. Everything the note above argues about
                   * WHICH fill is correct still holds; only its owner changed.
                   *
                   * ⚠️ PARTIALLY SUPERSEDED (2026-09-04). `background.paper` is
                   * still the LIGHT-mode fill and everything above is why. But
                   * it is not sufficient on its own: it is the correct fill only
                   * while the control sits on `background.default`, and inside a
                   * `Card` — which IS `background.paper` — it made the pill the
                   * same colour as its own container. The fill is now a function
                   * of the theme (`theme/segmentedChrome`), light branch
                   * unchanged. Do not fold it back to a bare token; "guaranteed
                   * by MUI and follows the theme" was necessary and not
                   * sufficient, which is the third time this one value has been
                   * changed for a reason the previous fix did not cover.
                   *
                   * `backgroundColor: transparent !important` is load-bearing
                   * rather than tidy-up. `apps/app`'s theme sets
                   * `MuiTab.styleOverrides.root['&.Mui-selected'].backgroundColor
                   *  = primary.main + '15 !important'` — a translucent black
                   * tint DARKER than the track. Without this, that consumer
                   * theme repaints a second, stationary "pill" on the selected
                   * half and the sliding one appears to move underneath a
                   * shadow of itself. A stylesheet `!important` is what
                   * outranks it (the same reason the margin above carries one).
                   *
                   * fontWeight: 500 selected against 400 unselected — the
                   * one-step delta the long note above sizes and justifies. Do
                   * not raise it back to `SegmentedTabs`' 600/500: that pair is
                   * calibrated for 14px and this control renders at 16.
                   */
                  "&.Mui-selected": {
                    color: `${theme.palette.text.primary} !important`,
                    backgroundColor: "transparent !important",
                    fontWeight: 500,
                  },
                }}
                value={tab.value}
                icon={tab.icon as any}
                iconPosition="start"
                label={tab.label}
                aria-label={tab.ariaLabel}
              />
            );
          })}
        </StyledMuiTabList>
      </TabContext>
    </Box>
  );
};
